import { compose } from "@ally/utilitarian";
import { getEnvironmentConfig } from "./environment";
/**
 * Adjust URL of an already existing URL
 * @param fn Function with url argument of type string and returns a string
 * @returns Returns modified URL as a Request Modifier
 */
export function adjustURL(fn) {
    const modifier = (args) => {
        const [input, init] = args;
        if (input instanceof URL) {
            return [new URL(fn(input.toString())), init];
        }
        return [fn(input), init];
    };
    return modifier;
}
/**
 * Sets the origin of a URL to the origin param
 *
 * @param origin URL or string, sets new url to have this origin
 * @returns Adjusted URL string value
 */
export const setOrigin = (origin) => adjustURL((url) => new URL(url, origin).toString());
/**
 * Modify a group of already existing headers
 *
 * @param fn Function that contains an argument for headers and returns headers
 * @returns Returns a request modifier from an array of input and init headers
 */
export function modifyHeaders(fn) {
    const modifier = (args) => {
        const [input, init] = args;
        const headers = new Headers(fn(new Headers(init === null || init === void 0 ? void 0 : init.headers)));
        return [input, Object.assign(Object.assign({}, init), { headers })];
    };
    return modifier;
}
/**
 * Add name and value to headers
 *
 * @param name String, will add this header name
 * @param value String, will add this header value
 * @returns Appended headers
 */
export const addHeader = (name, value) => modifyHeaders((headers) => {
    headers.append(name, value);
    return headers;
});
/**
 * Creates simplified fetch from request modifiers
 *
 * @param requestModifiers An array of RequestModifier[] that will be combined
 * @returns Returns a Simplified Fetch from multiple request modifiers
 */
export function composeFetch({ requestModifiers, }) {
    const combinedModifier = compose(...requestModifiers);
    const modifiedFetch = (...args) => {
        const modifiedArgs = combinedModifier(args);
        return fetch(...modifiedArgs);
    };
    return modifiedFetch;
}
/**
 * Gets environment config from environment and uses the api key and fetch origin to return an array of headers and sets the origin
 *
 * @param environment Environment, get API Key and fetch origin from this
 * @returns Return array of origin and headers
 */
export function getBaseRequestModifiers(environment) {
    const { apigeeApiKey, fetchOrigin } = getEnvironmentConfig(environment);
    return [
        setOrigin(fetchOrigin),
        addHeader("content-type", "application/json"),
        addHeader("API-KEY", apigeeApiKey),
    ];
}
/**
 * Takes the environment and gets the request modifiers which will be composed into a simplified fetch
 *
 * @param args BaseFetchArgs type, will use the environment from this
 * @returns Simplified fetch from the environments request modifiers
 */
export function baseFetch(args) {
    const requestModifiers = getBaseRequestModifiers(args.environment);
    return composeFetch({ requestModifiers });
}
//# sourceMappingURL=fetch.js.map